import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createFleetMaintenanceRule, updateFleetMaintenanceRule } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import { FleetMaintenanceRule, FleetMaintenanceRuleRequest, FleetTriggerType } from '../../../types/fleet';
import { preventMinus } from '../../../utils/formUtilis';
import FleetMaintenanceTypeSelect from '../shared/FleetMaintenanceTypeSelect';
import FleetTriggerTypeSelect from '../shared/FleetTriggerTypeSelect';

type Props = Readonly<{
  maintenanceRule?: FleetMaintenanceRule;
  onCancel: () => void;
  afterSave: () => void;
}>;

export function FleetMaintenanceRuleForm({ maintenanceRule, onCancel, afterSave }: Props) {
  const { css, theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [editingMaintenanceRule, setEditingMaintenanceRule] = useState<FleetMaintenanceRuleRequest>({
    clientId: apiParams?.clientId ?? '',
    subClientId: apiParams?.subClientId ?? '',
    description: '',
    maintenanceTypeId: '',
    name: '',
    trigger: 0,
    triggerType: FleetTriggerType.distance,
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    if (maintenanceRule && apiParams) {
      setEditingMaintenanceRule({
        clientId: maintenanceRule.client?._id ?? apiParams.clientId,
        subClientId: apiParams.subClientId,
        description: maintenanceRule.description,
        maintenanceTypeId: maintenanceRule.maintenanceType._id,
        name: maintenanceRule.name,
        trigger: maintenanceRule.trigger,
        triggerType: maintenanceRule.triggerType,
      });
    }
  }, [maintenanceRule, apiParams]);

  const canSubmit = useMemo(() => {
    return (
      !!editingMaintenanceRule.name &&
      !!editingMaintenanceRule.description &&
      !!editingMaintenanceRule.trigger &&
      !!editingMaintenanceRule.triggerType &&
      !!editingMaintenanceRule.maintenanceTypeId
    );
  }, [editingMaintenanceRule]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof FleetMaintenanceRuleRequest) => {
    setEditingMaintenanceRule((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    startLoading();
    const { id, error } = maintenanceRule
      ? await updateFleetMaintenanceRule(maintenanceRule._id, editingMaintenanceRule)
      : await createFleetMaintenanceRule(editingMaintenanceRule);
    if (!error && id) {
      afterSave();
      showPositiveFeedback(
        maintenanceRule
          ? FEEDBACK.edited(FEEDBACK_PREFIXES.maintenanceRule, editingMaintenanceRule.name)
          : FEEDBACK.created(FEEDBACK_PREFIXES.maintenanceRule),
      );
    } else {
      showFailFeedback(
        error || maintenanceRule
          ? FEEDBACK.failedEdition(FEEDBACK_PREFIXES.maintenanceRule, editingMaintenanceRule.name)
          : FEEDBACK.failedCreation(FEEDBACK_PREFIXES.maintenanceRule),
      );
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <LabeledInput
          label="Nombre:"
          value={editingMaintenanceRule?.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        <LabeledInput
          label="Descripción:"
          value={editingMaintenanceRule?.description}
          onChange={(value) => onInputChange(value, 'description')}
          required
        />
        <FleetMaintenanceTypeSelect
          selectedMaintenanceTypeId={editingMaintenanceRule.maintenanceTypeId}
          onChangeMaintenanceTypeId={(maintenanceTypeId) => {
            onInputChange(maintenanceTypeId, 'maintenanceTypeId');
          }}
          required
        />
        <FleetTriggerTypeSelect
          selectedId={editingMaintenanceRule.triggerType}
          onChangeId={(triggerTypeId) => {
            onInputChange(triggerTypeId, 'triggerType');
          }}
          disabled={!!maintenanceRule}
          required
        />
        <LabeledInput
          label="Valor de activación:"
          value={editingMaintenanceRule?.trigger}
          onChange={(value) => onInputChange(value, 'trigger')}
          type="number"
          min={0}
          onKeyDown={(event) => {
            preventMinus(event);
          }}
          endEnhancer={
            editingMaintenanceRule.triggerType === FleetTriggerType.distance
              ? 'km'
              : editingMaintenanceRule.triggerType === FleetTriggerType.days
                ? 'días'
                : undefined
          }
          required
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {maintenanceRule?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
